import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import GPSData from '../GPSData';
import PlayerTeamTraining from './PlayerTeamTraining';
import PlayerChronic from '../gps/PlayerChronic';
import MaxSpeed from '../gps/MaxSpeed';
import IndividualTargets from '../gps/IndividualTargets';
import MatchPerformance from '../gps/MatchPerformance';
import MatchChronic from '../gps/MatchChronic';
import InMatchAnalysis from '../gps/InMatchAnalysis';
import GoalkeeperReport from '../gps/GoalkeeperReport';
import WellnessStaff from '../WellnessStaff';

// EWMA is intentionally excluded — players should not see their own ACWR/load-risk data.
const ALL_PLAYER_GPS_TABS = [
  { id: 'team-training',      label: 'Team Training'    },
  { id: 'player-chronic',     label: 'Player Chronic'   },
  { id: 'max-speed',          label: 'Max Speed'        },
  { id: 'individual-targets', label: 'Individual Targets'},
  { id: 'match-performance',  label: 'Match Performance'},
  { id: 'match-chronic',      label: 'Match Chronic'    },
  { id: 'in-match-analysis',  label: 'In-Match Analysis'},
  { id: 'wellness',           label: 'Wellness'         },
  { id: 'rehab-training',     label: 'Rehab'            },
  // GK-only (2026-08-20) — gated in PlayerApp.js's allowedGpsIds, never offered to outfield players.
  { id: 'goalkeeper',         label: 'Goalkeeper'       },
];

const BACK_BTN = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  borderRadius: 10,
  border: '1px solid var(--border-strong)',
  background: 'var(--bg-card)',
  color: 'var(--text-secondary)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  minHeight: 44,
  flexShrink: 0,
};

export default function PlayerData({ playerRecord, isMobile, tabIds, backPath = '/player', user }) {
  const navigate = useNavigate();
  const { tab: urlTab } = useParams();
  const [searchParams] = useSearchParams();
  const initialSessionId = searchParams.get('session');
  const ACTIVE_TABS = tabIds
    ? ALL_PLAYER_GPS_TABS.filter(t => tabIds.includes(t.id))
    : ALL_PLAYER_GPS_TABS;
  // urlTab comes from a deep link (e.g. Today's "tap a session" link, which always points at
  // team-training) — validated against ACTIVE_TABS rather than trusted outright, so a
  // goalkeeper's tabIds=['goalkeeper'] restriction can't be bypassed by an old/stray link.
  const [activeTab, setActiveTab] = useState(
    (urlTab && ACTIVE_TABS.some(t => t.id === urlTab)) ? urlTab : (ACTIVE_TABS[0]?.id || 'player-chronic')
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'team-training':     return isMobile ? <PlayerTeamTraining playerRecord={playerRecord} initialSessionId={initialSessionId} /> : <GPSData playerRecord={playerRecord} initialSessionId={initialSessionId} />;
      case 'player-chronic':    return <PlayerChronic playerOverride={playerRecord} />;
      case 'max-speed':         return <MaxSpeed playerOverride={playerRecord} />;
      case 'individual-targets':return <IndividualTargets playerOverride={playerRecord} />;
      case 'match-performance': return <MatchPerformance user={user} playerRecord={playerRecord} />;
      case 'match-chronic':     return <MatchChronic playerOverride={playerRecord} />;
      case 'in-match-analysis': return <InMatchAnalysis />;
      case 'wellness':          return <WellnessStaff playerOverride={playerRecord} forceIndividual />;
      case 'rehab-training':    return isMobile ? <PlayerTeamTraining playerRecord={playerRecord} mode="rehab" /> : <GPSData playerRecord={playerRecord} mode="rehab" />;
      case 'goalkeeper':        return <GoalkeeperReport playerOverride={playerRecord} />;
      default:                  return <GPSData />;
    }
  };

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', paddingBottom: isMobile ? 80 : 40 }}>
      {/* Top row: back + tabs */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: isMobile ? '12px 12px 0' : '16px 16px 0',
      }}>
        <button onClick={() => navigate(backPath)} style={BACK_BTN}>← Back</button>

        {/* Scrollable tab bar */}
        <div style={{
          display: 'flex',
          gap: 6,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          flex: 1,
          paddingBottom: 2,
        }}>
          {ACTIVE_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: isMobile ? '8px 12px' : '6px 14px',
                borderRadius: 99,
                border: activeTab === tab.id ? '1px solid #3b82f6' : '1px solid var(--border)',
                background: activeTab === tab.id ? '#3b82f6' : 'transparent',
                color: activeTab === tab.id ? '#fff' : 'var(--text-muted)',
                fontWeight: activeTab === tab.id ? 600 : 400,
                fontSize: 13,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontFamily: 'inherit',
                minHeight: isMobile ? 40 : 32,
                flexShrink: 0,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: isMobile ? '12px 0 0' : '16px 16px 0' }}>
        {renderContent()}
      </div>
    </div>
  );
}
